import styled from "styled-components";
import SmallPopupTemplate from "../small/SmallPopupTemplate";
import { COLORS } from "styles/colors";
import { calcRem } from "styles/theme";
import { Text } from "components/atoms/text/Text";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  selectStomp,
  selectSubscriptionStomp,
} from "redux/stomp/stompSelector";
import { useMenu } from "hook/useMenu";
import { getSwitchAccountList } from "api/accountAPI";
import { useLocation } from "react-router-dom";
import {
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useAuth } from "components/Auth";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { checkIsSuspended, getAccountInfo } from "utils/AccountUtil";
import { setAccountSetting } from "redux/reducers/account/accountSlice";
import { useQueryClient } from "react-query";

type ModalProp = {};

const enum NEXT_ACTION {
  LOGOUT = "logout",
  SWITCH_ACCOUNT = "switch_account",
  RELOAD = "reload",
  NOTHING = "nothing",
}

const ChangeOrganizationModal = (props: ModalProp) => {
  const { suspendUpdated, reconnectedDate } = useSelector(selectStomp);
  const { isAuthenticated } = useAppSelector((state) => state.isAuth);
  const [nextAction, setNextAction] = useState<NEXT_ACTION>(
    NEXT_ACTION.NOTHING
  );
  const status = useRef<SUSPENSION_STATUS>(SUSPENSION_STATUS.ENABLED);
  const location = useLocation();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const {
    subscriptionActivatedUpdated,
    planAutoEnrollUpdated,
    planPeriodUpdated,
  } = useAppSelector(selectSubscriptionStomp);

  const { navigateSwitchAccount, checkPermissionLocation } = useMenu({});
  const [showModal, setShowModal] = useState<boolean>(false);
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const onDisabledAccount = (showModal: boolean = true) => {
    getSwitchAccountList({
      payload: {
        total: 0,
        pageNum: 0,
        pageLimit: 10000,
        keyword: "",
        sortType: "name",
        sortDirection: SORT_DIRECTION.ASC,
      },
    })
      .then(async (response) => {
        const accounts = (response?.result || []).filter(
          (account) => checkIsSuspended(account) !== SUSPENSION_STATUS.DISABLED
        );
        if (accounts.length === 0) {
          setNextAction(NEXT_ACTION.LOGOUT);
          return;
        }
        navigateSwitchAccount(
          {
            accountId: accounts[0].accountId,
            onSuccess: () => {
              if (showModal) {
                setNextAction(NEXT_ACTION.SWITCH_ACCOUNT);
              }
            },
            onFail: () => {
              setNextAction(NEXT_ACTION.LOGOUT);
            },
          },
          false
        );
      })
      .catch((err) => {
        auth.logout();
        setNextAction(NEXT_ACTION.LOGOUT);
      })
      .finally(() => {
        if(showModal) {
          setShowModal(true);
        }
      });
  };

  const reloadAccountInfo = useCallback(
    (forceReload: boolean) => {
      getAccountInfo(selectedAccount.accountId)
        .then((account) => {
          if (
            !account ||
            checkIsSuspended(account) === SUSPENSION_STATUS.DISABLED
          ) {
            onDisabledAccount();
            return;
          }
          // 권한/재연결 후 현재 캐시된 useQuery 전부 재조회
          void queryClient.invalidateQueries(undefined, {
            refetchActive: true,
            refetchInactive: true,
          });

          const isSuspendedChanged =
            checkIsSuspended(account) !== selectedAccount.isSuspended;

          if (isSuspendedChanged || forceReload) {
            setNextAction(NEXT_ACTION.RELOAD);
            dispatch(setAccountSetting(account));
            setShowModal(true);
          }
        })
        .catch((error) => {
          onDisabledAccount(error?.response?.data?.error === 3019);
        });
    },
    [
      selectedAccount.accountId,
      selectedAccount.isSuspended,
      dispatch,
      queryClient,
    ]
  );

  useEffect(() => {
    if (!auth.user.userId) return;
    if (!isAuthenticated) return;

    const hasSuspendUpdate = !!suspendUpdated.updatedDate;
    const hasSubscriptionUpdate = !!subscriptionActivatedUpdated.updatedDate;

    if (
      !reconnectedDate &&
      ((!hasSuspendUpdate && !hasSubscriptionUpdate) ||
        suspendUpdated.status === status.current)
    ) {
      return;
    }

    if (hasSuspendUpdate) {
      status.current = suspendUpdated.status as SUSPENSION_STATUS;
    }

    const isDisabledSuspendEvent =
      hasSuspendUpdate &&
      suspendUpdated.status === SUSPENSION_STATUS.DISABLED &&
      suspendUpdated.accountId === selectedAccount.accountId;

    if (!reconnectedDate && isDisabledSuspendEvent) {
      if (auth.user.totalAccount > 1) {
        onDisabledAccount();
      } else {
        setNextAction(NEXT_ACTION.LOGOUT);
        setShowModal(true);
      }
      return;
    }

    if (
      !reconnectedDate &&
      subscriptionActivatedUpdated.accountId !== selectedAccount.accountId &&
      !hasSuspendUpdate
    ) {
      return;
    }

    reloadAccountInfo(
      hasSuspendUpdate && suspendUpdated.accountId === selectedAccount.accountId
    );
  }, [
    suspendUpdated,
    subscriptionActivatedUpdated,
    reconnectedDate,
    auth.user.userId,
    auth.user.totalAccount,
    isAuthenticated,
    selectedAccount.accountId,
    selectedAccount.isSuspended,
    reloadAccountInfo,
  ]);

  useEffect(() => {
    if (!auth.user.userId) return;
    if (!isAuthenticated) return;

    const hasPlanAutoEnrollUpdate =
      !!planAutoEnrollUpdated.updatedDate &&
      planAutoEnrollUpdated.targetAccountId === selectedAccount.accountId;
    const hasPlanPeriodUpdate =
      !!planPeriodUpdated.updatedDate &&
      planPeriodUpdated.accountId === selectedAccount.accountId;

    if (
      !(
        (selectedAccount.accountLevel === LEVEL_TYPE.EU &&
          hasPlanAutoEnrollUpdate) ||
        (selectedAccount.accountLevel === LEVEL_TYPE.CP && hasPlanPeriodUpdate)
      )
    ) {
      return;
    }

    reloadAccountInfo(true);
  }, [
    planAutoEnrollUpdated,
    planPeriodUpdated,
    auth.user.userId,
    isAuthenticated,
    selectedAccount.accountId,
    selectedAccount.accountLevel,
    reloadAccountInfo,
  ]);

  const onClick = useCallback(
    (nextAction: NEXT_ACTION) => {
      setShowModal(false);
      if (nextAction === NEXT_ACTION.RELOAD) {
        checkPermissionLocation();
      }
      if (nextAction === NEXT_ACTION.LOGOUT) {
        auth.logout();
      }
    },
    [auth, checkPermissionLocation]
  );

  useEffect(() => {
    if (location.pathname.includes("/login")) {
      setShowModal(false);
      setNextAction(NEXT_ACTION.NOTHING);
    }
  }, [location.pathname]);

  if (showModal)
    return (
      <SmallPopupTemplate
        title={""}
        onClick={() => onClick(nextAction)}
        confirmLabel="OK"
        data-testid="templates-modal-auth-small-popup-template-click"
      >
        <ModalText>
          {(nextAction === NEXT_ACTION.LOGOUT ||
            nextAction === NEXT_ACTION.SWITCH_ACCOUNT) &&
            "Your organization has been disabled."}
          {nextAction === NEXT_ACTION.RELOAD &&
            "Your organization status has been changed."}
          {nextAction === NEXT_ACTION.SWITCH_ACCOUNT && (
            <Text>You are being switched to another organization.</Text>
          )}
        </ModalText>
      </SmallPopupTemplate>
    );

  return null;
};

export default ChangeOrganizationModal;

const ModalText = styled.div`
  color: ${COLORS.RED};
  font-weight: 500;
  text-align: center;
  font-size: ${calcRem(20)};
  width: 100%;
  line-height: ${calcRem(24)};

  > div {
    padding-top: 10px;
  }
`;
